import { useState } from 'react';
import {
  Button,
  Card,
  Flex,
  Input,
  message,
  Pagination,
  Switch,
  Table,
  Tabs,
  Typography,
} from 'antd';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  EmailMgmtFranchiseRow,
  EmailMgmtUserRow,
  downloadEmailMgmtConsultants,
  downloadEmailMgmtFranchisors,
  getEmailMgmtConsultants,
  getEmailMgmtFranchisors,
  updateEmailMgmtBrandSync,
  updateEmailMgmtUserSync,
} from '@/api/emailManagement';
import './index.scss';

// CSV download helper: base64 -> Blob -> anchor click. Same pattern as
// the Rating Management Download Table (PR #509) for consistency across
// admin exports.
const triggerCsvDownload = (base64: string, filename: string) => {
  const csv = atob(base64);
  const blob = new Blob([csv], { type: 'text/csv' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

type EmailMgmtTab = 'consultants' | 'franchisors';

export default function AdminEmailManagement() {
  const [activeTab, setActiveTab] = useState<EmailMgmtTab>('consultants');

  return (
    <>
      <Flex
        justify={'space-between'}
        align={'center'}
        style={{ marginBottom: 24 }}
      >
        <Typography.Title level={2} style={{ margin: 0 }}>
          Email Management
        </Typography.Title>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k as EmailMgmtTab)}
        className={'email-management-tabs'}
        items={[
          { key: 'consultants', label: 'Consultants' },
          { key: 'franchisors', label: 'Franchisors' },
        ]}
      />

      <div className={'email-management-section-heading'}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {activeTab === 'consultants' ? 'Consultants' : 'Franchisors'}
        </Typography.Title>
      </div>

      {activeTab === 'consultants' ? <ConsultantsTab /> : <FranchisorsTab />}
    </>
  );
}

function ConsultantsTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const queryKey = ['emailMgmt', 'consultants', search, page, limit] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      getEmailMgmtConsultants({ search: search || undefined, page, limit }),
  });

  const { mutate: setSync } = useMutation({
    mutationFn: ({ id, mailgunSync }: { id: string; mailgunSync: boolean }) =>
      updateEmailMgmtUserSync(id, mailgunSync),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailMgmt'] });
    },
    onError: (e: any) => {
      message.error(e?.message ?? 'Could not update Mailgun sync');
    },
  });

  const columns: ColumnsType<EmailMgmtUserRow> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim(),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Mailgun Sync',
      key: 'mailgunSync',
      align: 'right',
      render: (_, r) => (
        <Switch
          checked={r.mailgunSync}
          onChange={(checked) => setSync({ id: r.id, mailgunSync: checked })}
        />
      ),
    },
  ];

  // Download Table — required by AC #8 (Search, Download, Pagination
  // alongside other admin pages). Hits the new
  // /admin/email-management/consultants/download endpoint with the
  // current search filter so the export reflects what's on screen.
  const { mutateAsync: triggerDownload, isPending: isDownloading } = useMutation(
    {
      mutationFn: () =>
        downloadEmailMgmtConsultants({ search: search || undefined }),
      onError: (e: any) => {
        message.error(e?.message ?? 'Failed to download table');
      },
    },
  );

  const handleDownload = async () => {
    try {
      const { file } = await triggerDownload();
      if (!file) throw new Error('Empty file from server');
      triggerCsvDownload(
        file,
        `email-management-consultants-${new Date().toISOString()}.csv`,
      );
      message.success('Download started');
    } catch (e: any) {
      message.error(e?.message ?? 'Failed to download table');
    }
  };

  return (
    <Card style={{ marginTop: 16 }}>
      <Flex
        justify={'space-between'}
        align={'center'}
        style={{ marginBottom: 16 }}
      >
        <Input.Search
          placeholder={'Search by name or email'}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 300 }}
        />
        <Button
          icon={<CloudDownloadOutlined />}
          className={'admin-button-download-table'}
          onClick={handleDownload}
          loading={isDownloading}
          disabled={isDownloading}
        >
          Download Table
        </Button>
      </Flex>
      <Table
        rowKey={'id'}
        loading={isLoading}
        dataSource={data?.users ?? []}
        columns={columns}
        pagination={false}
      />
      <Flex justify={'flex-end'} style={{ marginTop: 16 }}>
        <Pagination
          current={page}
          pageSize={limit}
          total={data?.total ?? 0}
          showSizeChanger
          onChange={(p, ps) => {
            setPage(p);
            if (ps !== limit) setLimit(ps);
          }}
        />
      </Flex>
    </Card>
  );
}

function FranchisorsTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const queryKey = ['emailMgmt', 'franchisors', search, page, limit] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      getEmailMgmtFranchisors({ search: search || undefined, page, limit }),
  });

  const { mutate: setBrandSync } = useMutation({
    mutationFn: ({ id, mailgunSync }: { id: string; mailgunSync: boolean }) =>
      updateEmailMgmtBrandSync(id, mailgunSync),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailMgmt'] });
    },
    onError: (e: any) => {
      message.error(e?.message ?? 'Could not update brand Mailgun sync');
    },
  });

  const { mutate: setUserSync } = useMutation({
    mutationFn: ({ id, mailgunSync }: { id: string; mailgunSync: boolean }) =>
      updateEmailMgmtUserSync(id, mailgunSync),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailMgmt'] });
    },
    onError: (e: any) => {
      message.error(e?.message ?? 'Could not update user Mailgun sync');
    },
  });

  const columns: ColumnsType<EmailMgmtFranchiseRow> = [
    {
      title: 'Brand Name',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Mailgun Sync',
      key: 'mailgunSync',
      align: 'right',
      render: (_, r) => (
        <Switch
          checked={r.mailgunSync}
          onChange={(checked) =>
            setBrandSync({ id: r.id, mailgunSync: checked })
          }
          onClick={(_, e) => e?.stopPropagation?.()}
        />
      ),
    },
  ];

  const expandedRowRender = (row: EmailMgmtFranchiseRow) => {
    const userColumns: ColumnsType<EmailMgmtUserRow> = [
      {
        title: 'Name',
        key: 'name',
        render: (_, u) => `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
      },
      { title: 'Email', dataIndex: 'email', key: 'email' },
      {
        title: 'Mailgun Sync',
        key: 'mailgunSync',
        align: 'right',
        render: (_, u) => (
          <Switch
            checked={u.mailgunSync}
            onChange={(checked) =>
              setUserSync({ id: u.id, mailgunSync: checked })
            }
          />
        ),
      },
    ];
    return (
      <Table
        rowKey={'id'}
        dataSource={row.users}
        columns={userColumns}
        pagination={false}
        size={'small'}
      />
    );
  };

  // Download Table — flattens brand rows + per-user rows into a single
  // CSV so the brand-level toggle + each user's override are both
  // captured in the export.
  const { mutateAsync: triggerDownload, isPending: isDownloading } = useMutation(
    {
      mutationFn: () =>
        downloadEmailMgmtFranchisors({ search: search || undefined }),
      onError: (e: any) => {
        message.error(e?.message ?? 'Failed to download table');
      },
    },
  );

  const handleDownload = async () => {
    try {
      const { file } = await triggerDownload();
      if (!file) throw new Error('Empty file from server');
      triggerCsvDownload(
        file,
        `email-management-franchisors-${new Date().toISOString()}.csv`,
      );
      message.success('Download started');
    } catch (e: any) {
      message.error(e?.message ?? 'Failed to download table');
    }
  };

  return (
    <Card style={{ marginTop: 16 }}>
      <Flex
        justify={'space-between'}
        align={'center'}
        style={{ marginBottom: 16 }}
      >
        <Input.Search
          placeholder={'Search by brand'}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 300 }}
        />
        <Button
          icon={<CloudDownloadOutlined />}
          className={'admin-button-download-table'}
          onClick={handleDownload}
          loading={isDownloading}
          disabled={isDownloading}
        >
          Download Table
        </Button>
      </Flex>
      <Table
        rowKey={'id'}
        loading={isLoading}
        dataSource={data?.franchises ?? []}
        columns={columns}
        pagination={false}
        expandable={{
          expandedRowRender,
          rowExpandable: (r) => (r.users?.length ?? 0) > 0,
        }}
      />
      <Flex justify={'flex-end'} style={{ marginTop: 16 }}>
        <Pagination
          current={page}
          pageSize={limit}
          total={data?.total ?? 0}
          showSizeChanger
          onChange={(p, ps) => {
            setPage(p);
            if (ps !== limit) setLimit(ps);
          }}
        />
      </Flex>
    </Card>
  );
}
